import Dialog from "../../General/ConfirmableDialog";
import React, { useEffect, useState } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";

interface IPreviewGalleryModalProps {
  open: boolean;
  onClose: () => void;
  truckForSale: any;
}

interface TruckForSaleImage {
  id: string;
  truckForSaleId: string;
  fileName: string;
}

const loadAuthorizedImage = async (
  fileName: string,
  token: string | null | undefined
): Promise<string> => {
  const response = await fetch(
    `${apiVariable}/truckForSale/preview?fileName=${encodeURIComponent(
      fileName
    )}`,
    {
      headers: {
        Authorization: `Bearer ${token ?? ""}`,
      },
    }
  );
  if (!response.ok) {
    throw new Error(`Image not found for fileName=${fileName}`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

const PreviewGalleryModal: React.FC<IPreviewGalleryModalProps> = ({
  open,
  onClose,
  truckForSale,
}) => {
  const { token } = useAuth();
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  const {
    data: images,
    isLoading,
    isError,
    refetch,
  } = useQuery<TruckForSaleImage[]>({
    queryKey: ["truckForSaleImages", truckForSale?.id],
    queryFn: async () => {
      const response = await axios.get(
        `${apiVariable}/truckForSale/${truckForSale.id}/images`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: open && !!truckForSale?.id,
  });

  useEffect(() => {
    if (open && truckForSale?.id) {
      refetch();
    }
  }, [open, truckForSale?.id, refetch]);

  useEffect(() => {
    if (images && images.length > 0) {
      const loadAllImages = async () => {
        const newImageUrls: Record<string, string> = {};
        for (const img of images) {
          try {
            const blobUrl = await loadAuthorizedImage(img.fileName, token);
            newImageUrls[img.id] = blobUrl;
          } catch (error) {
            console.error("Error loading image:", error);
          }
        }
        setImageUrls(newImageUrls);
      };

      loadAllImages();
    }
  }, [images, token]);

  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imageUrls]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Gallery: {truckForSale?.make} {truckForSale?.model}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box textAlign="center">
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box color="error.main">Error loading images.</Box>
        ) : !images?.length ? (
          <Box>No images found.</Box>
        ) : (
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            justifyContent="flex-start"
          >
            {images.map((img) => (
              <Box key={img.id}>
                <img
                  src={imageUrls[img.id] ?? ""}
                  alt="truck for sale"
                  style={{
                    width: 200,
                    height: "auto",
                    border: "1px solid #ccc",
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewGalleryModal;
